import React, { useEffect, useState } from "react";
import Select from "react-select";
import momentTz from "moment-timezone";
import { getTimeZoneFromCountry } from "../../../services/auth";

// Guest booking's timezone double-check (guestbooking/bookingpage.js): a
// recipient in one of these countries whose timezone isn't in its list is
// asked to reconfirm. Same lists and wording as guest booking.
const COUNTRY_TIMEZONES = {
  "United States": [
    "(GMT-5:00) Eastern Standard Time - New York",
    "(GMT-5:00) Eastern Standard Time - East-Indiana",
    "(GMT-6:00) Central Standard Time - Chicago",
    "(GMT-9:00) Alaska Standard Time - Alaska",
    "(GMT-10:00) Hawaii Standard Time - Honolulu",
    "(GMT-11:00) Samoa Standard Time - Midway",
    "(GMT-06:00) Central Standard Time - Chicago",
    "(GMT-05:00) Eastern Standard Time - New_York",
  ],
  Canada: [
    "(GMT-3:30) Newfoundland Standard Time - Newfoundland",
    "(GMT-4:00) Atlantic Standard Time - Atlantic",
    "(GMT-5:00) Eastern Standard Time - New York",
    "(GMT-6:00) Central Standard Time - Saskatchewan",
    "(GMT-6:00) Central Standard Time - Chicago",
    "(GMT-06:00) Central Standard Time - Chicago",
  ],
};

// Same search as guest booking's country picker: name, code, "U.S.A."-style
// initials, with or without dots.
const countryOption = (item) => {
  const initials =
    item.country_name === "United States"
      ? "USA"
      : item.country_name
          .split(" ")
          .map((word) => word.charAt(0))
          .join("");
  return {
    value: item.id,
    label: item.country_name,
    country_code: item.country_code,
    dot_name: item.country_code.split("").join("."),
    updatedName: initials,
    updatedDotName: item.country_name === "United States" ? "U.S.A." : initials.split("").join(".").concat("."),
  };
};

const filterCountry = (option, input) => {
  if (!input) return true;
  const needle = input.toLowerCase();
  return [option.label, option.data.country_code, option.data.dot_name, option.data.updatedDotName, option.data.updatedName]
    .map((value) => String(value || "").toLowerCase())
    .some((value) => value.startsWith(needle) || value.includes(needle));
};

const timeIn = (zoneName) => {
  try {
    return momentTz().tz(zoneName).format("hh:mm A");
  } catch (err) {
    return "";
  }
};

// "About you" for every recipient: country and timezone laid out like the
// first step of guest booking (so dates and times are right). askLanguages
// (no VerySoul account yet): also the languages used to suggest mediums.
export default function GiftAboutYou({
  countries,
  timezones,
  languages,
  languagesLoading = false,
  onReloadLanguages,
  value,
  onChange,
  onNext,
  busy,
  askLanguages = true,
}) {
  const [errors, setErrors] = useState({});
  const [reconfirm, setReconfirm] = useState(null); // { list, choice, confirmed, error }

  const countryOptions = countries.map(countryOption);
  const timezoneOptions = timezones.map((tz) => ({ value: tz.id, label: tz.front_name || tz.name, name: tz.name }));
  const languageOptions = languages.map((lang) => ({ value: lang.id, label: lang.name }));

  // Empty language list (first request failed or still empty): fetch it again from the API.
  const reloadLanguagesIfEmpty = () => {
    if (askLanguages && !languages.length && !languagesLoading && onReloadLanguages) onReloadLanguages();
  };
  useEffect(reloadLanguagesIfEmpty, [askLanguages]);

  const set = (patch) => {
    onChange({ ...value, ...patch });
    setErrors({});
  };

  const next = async () => {
    const found = {};
    if (!value.country) found.country = "Please choose your country.";
    if (!value.timezone) found.timezone = "*Please select timezone to proceed";
    if (askLanguages && !value.languages.length) found.languages = "Please choose at least one language.";
    setErrors(found);
    if (Object.keys(found).length) return;

    // Guest booking's US / Canada check: reconfirm when the timezone isn't one of that country's.
    const expected = COUNTRY_TIMEZONES[value.countryName];
    const chosen = timezones.find((tz) => String(tz.id) === String(value.timezone));
    if (expected && chosen && expected.indexOf(chosen.front_name) === -1) {
      const res = await getTimeZoneFromCountry(value.countryCode);
      const list = res && res.data && res.data.status ? res.data.data || [] : [];
      setReconfirm({ list: list.length ? list : timezones, choice: "", confirmed: false, error: "" });
      return;
    }
    onNext(value);
  };

  const submitReconfirm = () => {
    if (!reconfirm.choice || !reconfirm.confirmed) {
      setReconfirm({ ...reconfirm, error: !reconfirm.choice ? "Required" : "Please confirm the time above." });
      return;
    }
    const updated = { ...value, timezone: String(reconfirm.choice) };
    onChange(updated);
    setReconfirm(null);
    onNext(updated);
  };

  const reconfirmZone = reconfirm && reconfirm.list.find((tz) => String(tz.id) === String(reconfirm.choice));

  return (
    <div className="vsr-center">
      <section className="vsr-card vsr-about">
        <h1 className="vsr-title">Please select your Timezone and Country to continue</h1>
        <p className="vsr-lead">This helps us show you accurate availability and appointment timings.</p>

        <div className="vsr-about-grid">
          <div className="vsr-field">
            <label className="vsr-label" htmlFor="about-country">
              Country<i>*</i>
            </label>
            <Select
              inputId="about-country"
              className="vsr-react-select"
              classNamePrefix="vsr-rs"
              options={countryOptions}
              placeholder="Choose Country"
              value={countryOptions.find((opt) => String(opt.value) === String(value.country)) || null}
              onChange={(opt) => set({ country: opt.value, countryName: opt.label, countryCode: opt.country_code })}
              filterOption={filterCountry}
              isSearchable
            />
            {errors.country && <span className="vsr-error">{errors.country}</span>}
          </div>

          <div className="vsr-field">
            <label className="vsr-label" htmlFor="about-timezone">
              Timezone<i>*</i>
            </label>
            <Select
              inputId="about-timezone"
              className="vsr-react-select"
              classNamePrefix="vsr-rs"
              options={timezoneOptions}
              placeholder="Change Timezone"
              value={timezoneOptions.find((opt) => String(opt.value) === String(value.timezone)) || null}
              onChange={(opt) => set({ timezone: String(opt.value) })}
              isSearchable
            />
            {errors.timezone ? (
              <span className="vsr-error">{errors.timezone}</span>
            ) : (
              value.timezone && (
                <span className="vsr-hint">
                  Current time:{" "}
                  <b>{timeIn((timezoneOptions.find((opt) => String(opt.value) === String(value.timezone)) || {}).name)}</b>
                </span>
              )
            )}
          </div>

          {askLanguages && (
            <div className="vsr-field vsr-about-languages">
              <label className="vsr-label" htmlFor="about-languages">
                Language(s) for your session<i>*</i>
              </label>
              <Select
                inputId="about-languages"
                className="vsr-react-select"
                classNamePrefix="vsr-rs"
                options={languageOptions}
                placeholder="Choose Languages"
                isMulti
                closeMenuOnSelect={false}
                isLoading={languagesLoading}
                onMenuOpen={reloadLanguagesIfEmpty}
                noOptionsMessage={() => (languagesLoading ? "Loading languages…" : "No languages found")}
                value={languageOptions.filter((opt) => value.languages.indexOf(opt.value) !== -1)}
                onChange={(opts) => set({ languages: (opts || []).map((opt) => opt.value) })}
              />
              {errors.languages ? (
                <span className="vsr-error">{errors.languages}</span>
              ) : (
                <span className="vsr-hint">We'll show you mediums who speak your language first.</span>
              )}
            </div>
          )}
        </div>

        <div className="vsr-actions">
          <button type="button" className="vsr-btn" disabled={busy} onClick={next}>
            {busy ? "Please wait…" : "Next"}
          </button>
        </div>
      </section>

      {reconfirm && (
        <div className="vsr-modal-backdrop">
          <div className="vsr-modal" role="dialog" aria-modal="true" aria-labelledby="vsr-reconfirm-title">
            <div className="vsr-modal-head">
              <h2 id="vsr-reconfirm-title">Please reconfirm your timezone</h2>
            </div>
            <div className="vsr-modal-body">
              <p className="vsr-lead vsr-lead-left">
                You've indicated that you are in {value.countryName}. Can you please reconfirm your timezone?
              </p>
              <div className="vsr-field">
                <label className="vsr-label" htmlFor="reconfirm-timezone">
                  Timezone<i>*</i>
                </label>
                <select
                  id="reconfirm-timezone"
                  className="vsr-select"
                  value={reconfirm.choice}
                  onChange={(e) => setReconfirm({ ...reconfirm, choice: e.target.value, error: "" })}
                >
                  <option value="" disabled>
                    Timezone
                  </option>
                  {reconfirm.list.map((tz) => (
                    <option key={tz.id} value={tz.id}>
                      {tz.front_name}
                    </option>
                  ))}
                </select>
              </div>
              {reconfirmZone && (
                <>
                  <p className="vsr-muted">
                    Current time as per selected timezone: <b>{timeIn(reconfirmZone.name)}</b>
                  </p>
                  <label className="vsr-check">
                    <input
                      type="checkbox"
                      checked={reconfirm.confirmed}
                      onChange={(e) => setReconfirm({ ...reconfirm, confirmed: e.target.checked, error: "" })}
                    />
                    <span>The time above is my correct time.</span>
                  </label>
                </>
              )}
              {reconfirm.error && <span className="vsr-error">{reconfirm.error}</span>}
              <div className="vsr-modal-actions">
                <button type="button" className="vsr-btn vsr-btn-outline" onClick={() => setReconfirm(null)}>
                  Back
                </button>
                <button type="button" className="vsr-btn" onClick={submitReconfirm}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
